import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { navigateTo } from '../../../libs/history';
import { getTrackedInternalLinkUrl } from '../../../libs/tracking';
import { formatCountdownMsTime } from '../../../libs/utils';
import { useMedia } from '../../grid/hooks';
import { Link } from '../../link';
import CountdownTimer from '../../presentational/countdownTimer';
import { asset } from '../../../services/assetResolverService';
import { useTrackedImpression } from '../../../scripts/hooks/useTrackedImpression';

type TAwcMidnightCup3UpsellProps = {
  className?: string;
};

type TAwcCupVariant = {
  label: string;
  analyticsKey: string;
  broadcastStartsAt: string;
  broadcastEndsAt: string;
  watchUrl: string;
  bracketUrl: string;
};

type TAwcCupVariantStatus = 'upcoming' | 'live' | 'completed';

type TAwcCupVariantStatusItem = {
  variant: TAwcCupVariant;
  status: Exclude<TAwcCupVariantStatus, 'completed'>;
};

type TAwcUpsellClickPart = 'body' | 'text';

type TUpsellTranslations = {
  liveBadge: string;
  startsInLabel: string;
  headline: string;
  supportingLine: string;
  button: string;
  bracketLink: string;
  logoAlt: string;
};

const NAMESPACE = 'dyn_awc_midnight_grand_finals_upsell';
const ANALYTICS_PREFIX = 'awc_midnight_grand_finals_upsell';
const LOGO_PATH = '/images/events/hub-page/expansion11/season1/logo_awc.png';
const BACKGROUND_PATH = '/images/events/expansion10/season-tww-3-mdi-awc/awc_bg.png';

const CUP_VARIANTS: TAwcCupVariant[] = [
  {
    label: 'Gauntlet',
    analyticsKey: 'gauntlet',
    broadcastStartsAt: '2026-06-05T17:00:00Z',
    broadcastEndsAt: '2026-06-06T04:00:00Z',
    watchUrl: '/tournaments/awc',
    bracketUrl: '/events/awc-midnight-season-1-grand-finals/brackets/europe-gauntlet'
  },
  {
    label: 'Grand Finals',
    analyticsKey: 'grand_finals',
    broadcastStartsAt: '2026-06-06T17:00:00Z',
    broadcastEndsAt: '2026-06-08T04:00:00Z',
    watchUrl: '/tournaments/awc',
    bracketUrl: '/events/awc-midnight-season-1-grand-finals/brackets/europe-grand-finals'
  }
];

const CUP_VARIANTS_BY_START = [...CUP_VARIANTS].sort(
  (left, right) => Date.parse(left.broadcastStartsAt) - Date.parse(right.broadcastStartsAt)
);

const EN_TRANSLATIONS: TUpsellTranslations = {
  liveBadge: '{{label}} Live',
  startsInLabel: '{{label}} Starts in',
  headline: 'Watch the AWC Season 1 Grand Finals!',
  supportingLine:
    'See the top teams battle through the <0>Gauntlet</0> and <1>Grand Finals</1> to be crowned champions on the <2>Road to BlizzCon</2>.',
  button: 'WATCH LIVE',
  bracketLink: 'View Bracket',
  logoAlt: ''
};

const UPSELL_TRANSLATIONS: Record<string, TUpsellTranslations> = {
  en: EN_TRANSLATIONS,
  'en-us': EN_TRANSLATIONS
};

const panelStyle: React.CSSProperties = {
  border: '1px solid #8e3453',
  borderRadius: '0.25rem',
  backgroundColor: '#10060c',
  backgroundSize: 'cover',
  backgroundPosition: 'center right',
  backgroundRepeat: 'no-repeat',
  color: '#fff',
  cursor: 'pointer',
  columnGap: '0.75rem',
  overflow: 'hidden'
};

const logoWrapStyle: React.CSSProperties = {
  width: 124,
  minWidth: 124,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: '-0.05rem'
};

const logoStyle: React.CSSProperties = {
  display: 'block',
  width: 116,
  height: 116,
  objectFit: 'contain',
  filter: 'drop-shadow(0 8px 18px rgba(0, 0, 0, 0.34))',
  transform: 'scale(1.16)',
  transformOrigin: 'center center'
};

const headerRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.45rem',
  flexWrap: 'wrap',
  marginBottom: '0.42rem'
};

const badgeBaseStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.3rem',
  padding: '0.16rem 0.46rem',
  borderRadius: '999px',
  fontSize: '0.72rem',
  fontWeight: 'bold',
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1.1,
  whiteSpace: 'nowrap',
  transition: 'all 120ms ease-out'
};

const primaryLiveBadgeStyle: React.CSSProperties = {
  ...badgeBaseStyle,
  border: '1px solid rgba(255, 149, 186, 0.46)',
  background: 'rgba(75, 18, 37, 0.94)',
  color: '#ffe0e8',
  boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.03)'
};

const primaryCountdownBadgeStyle: React.CSSProperties = {
  ...badgeBaseStyle,
  border: '1px solid rgba(255, 205, 95, 0.5)',
  background: 'rgba(92, 56, 12, 0.9)',
  color: '#ffe29a',
  boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.04)'
};

const secondaryCountdownBadgeStyle: React.CSSProperties = {
  ...badgeBaseStyle,
  border: '1px solid rgba(255, 205, 95, 0.28)',
  background: 'rgba(18, 12, 8, 0.78)',
  color: 'rgba(255, 226, 154, 0.82)',
  boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.025)'
};

const contentStyle: React.CSSProperties = {
  flex: '1 1 auto',
  maxWidth: '48rem',
  minWidth: 0
};

const headlineStyle: React.CSSProperties = {
  lineHeight: 1.08,
  maxWidth: '34rem',
  marginBottom: '0.36rem'
};

const supportingLineStyle: React.CSSProperties = {
  lineHeight: 1.18,
  maxWidth: '36rem',
  color: '#f4e8ec'
};

const highlightStyle: React.CSSProperties = {
  color: '#ffcc69'
};

const supportHighlightStyle: React.CSSProperties = {
  color: '#fff'
};

const secondaryHighlightStyle: React.CSSProperties = {
  color: '#00b4ff',
  textShadow: '0 0 5px rgba(0, 180, 255, 0.12)',
  whiteSpace: 'nowrap'
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35rem',
  alignItems: 'stretch'
};

const primaryButtonStyle: React.CSSProperties = {
  backgroundColor: '#ce9908',
  fontSize: '0.95rem',
  minWidth: 138
};

const supportLinkStyle: React.CSSProperties = {
  color: 'rgba(255, 244, 248, 0.9)',
  fontSize: '0.75rem',
  fontWeight: 'bold',
  textAlign: 'center',
  textDecoration: 'none',
  textShadow: '0 0 6px rgba(255, 255, 255, 0.08)'
};

const ctaButtonClassName = [
  'slds-button',
  'slds-button--raiderio',
  'text-white',
  'slds-p-vertical--x-small',
  'slds-p-horizontal--small',
  'text-bold',
  'rio-text--title',
  'slds-text-heading--small',
  'text-nowrap'
].join(' ');

function getSelectedCupVariant(now: number) {
  return getVisibleCupVariantStatuses(now)[0]?.variant || CUP_VARIANTS_BY_START[CUP_VARIANTS_BY_START.length - 1];
}

function getVisibleCupVariantStatuses(now: number): TAwcCupVariantStatusItem[] {
  const activeVariant = CUP_VARIANTS_BY_START.find((variant) => {
    const startsAt = Date.parse(variant.broadcastStartsAt);
    const endsAt = Date.parse(variant.broadcastEndsAt);
    return now >= startsAt && now < endsAt;
  });
  const currentVariant =
    activeVariant || CUP_VARIANTS_BY_START.find((variant) => now < Date.parse(variant.broadcastStartsAt));

  if (!currentVariant) {
    return [];
  }

  const currentStatus = getVariantStatus(currentVariant, now);
  if (currentStatus === 'completed') {
    return [];
  }

  const statusItems: TAwcCupVariantStatusItem[] = [
    {
      variant: currentVariant,
      status: currentStatus
    }
  ];
  const nextUpcomingVariant = CUP_VARIANTS_BY_START.find((variant) => {
    return (
      Date.parse(variant.broadcastStartsAt) > Date.parse(currentVariant.broadcastStartsAt) &&
      now < Date.parse(variant.broadcastStartsAt)
    );
  });

  if (nextUpcomingVariant) {
    statusItems.push({
      variant: nextUpcomingVariant,
      status: 'upcoming'
    });
  }

  return statusItems;
}

function getVariantStatus(variant: TAwcCupVariant, now: number): TAwcCupVariantStatus {
  const startsAt = Date.parse(variant.broadcastStartsAt);
  const endsAt = Date.parse(variant.broadcastEndsAt);

  if (now < startsAt) {
    return 'upcoming';
  }

  if (now < endsAt) {
    return 'live';
  }

  return 'completed';
}

function getStatusTransitionAt(variant: TAwcCupVariant, status: TAwcCupVariantStatus): number | null {
  if (status === 'upcoming') {
    return Date.parse(variant.broadcastStartsAt);
  }

  if (status === 'live') {
    return Date.parse(variant.broadcastEndsAt);
  }

  return null;
}

function getNextStatusTransitionAt(statusItems: TAwcCupVariantStatusItem[]): number | null {
  const transitions = statusItems
    .map(({ variant, status }) => getStatusTransitionAt(variant, status))
    .filter((transitionAt): transitionAt is number => transitionAt !== null);

  if (transitions.length === 0) {
    return null;
  }

  return Math.min(...transitions);
}

function getBadgeStyle(status: TAwcCupVariantStatus, isPrimary: boolean) {
  if (status === 'live') {
    return primaryLiveBadgeStyle;
  }

  return isPrimary ? primaryCountdownBadgeStyle : secondaryCountdownBadgeStyle;
}

function getVisibleStatusAnalyticsValue(statusItems: TAwcCupVariantStatusItem[]) {
  if (statusItems.length === 0) {
    return 'none';
  }

  return statusItems.map(({ variant, status }) => `${variant.analyticsKey}:${status}`).join('|');
}

export function AwcMidnightCup3Upsell(props: TAwcMidnightCup3UpsellProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useMedia(['(max-width: 767px)'], [true], false);
  const isStackedCta = useMedia(['(max-width: 1099px)'], [true], false);
  const shouldStackCta = !isMobile && isStackedCta;
  const [currentTime, setCurrentTime] = useState(Date.now);
  const visibleCupStatuses = getVisibleCupVariantStatuses(currentTime);
  const selectedCup = getSelectedCupVariant(currentTime);
  const nextStatusTransitionAt = getNextStatusTransitionAt(visibleCupStatuses);
  const visibleStatusAnalytics = getVisibleStatusAnalyticsValue(visibleCupStatuses);
  const panelImpressionRef = useTrackedImpression<HTMLDivElement>({
    onImpression: () => {
      RIO_TRACK_EVENT('event', 'upsell_impression', {
        content_type: 'upsell',
        content_id: `${ANALYTICS_PREFIX}:${selectedCup.analyticsKey}`,
        upsell_id: ANALYTICS_PREFIX,
        upsell_variant: selectedCup.analyticsKey,
        upsell_visible_statuses: visibleStatusAnalytics,
        destination_path: selectedCup.watchUrl
      });
    }
  });

  useEffect(() => {
    if (!nextStatusTransitionAt) {
      return undefined;
    }

    const timeoutMs = Math.max(nextStatusTransitionAt - currentTime, 0) + 1000;
    const timeout = window.setTimeout(() => setCurrentTime(Date.now()), timeoutMs);
    return () => window.clearTimeout(timeout);
  }, [currentTime, nextStatusTransitionAt]);

  Object.entries(UPSELL_TRANSLATIONS).forEach(([lang, translations]) => {
    if (!i18n.hasResourceBundle(lang, NAMESPACE)) {
      i18n.addResourceBundle(lang, NAMESPACE, translations);
    }
  });

  const panelBackgroundStyle = {
    ...panelStyle,
    backgroundImage: [
      'radial-gradient(circle at 87% 50%, rgba(220, 150, 72, 0.14) 0%, rgba(220, 150, 72, 0.04) 23%, rgba(220, 150, 72, 0) 48%)',
      'linear-gradient(90deg, rgba(8, 4, 8, 0.96) 0%, rgba(18, 6, 13, 0.9) 32%, rgba(41, 12, 25, 0.58) 66%, rgba(50, 22, 16, 0.5) 100%)',
      `url(${asset(BACKGROUND_PATH, { width: 1800, height: 360, quality: 92 })})`
    ].join(', ')
  };
  const panelResponsiveStyle = {
    ...panelBackgroundStyle,
    justifyContent: isMobile ? 'center' : undefined,
    rowGap: isMobile ? '0.5rem' : undefined
  };
  const logoWrapResponsiveStyle = {
    ...logoWrapStyle,
    marginLeft: isMobile ? 0 : logoWrapStyle.marginLeft
  };
  const logoResponsiveStyle: React.CSSProperties = {
    ...logoStyle,
    transform: isMobile ? 'scale(1.1)' : logoStyle.transform
  };
  const contentResponsiveStyle: React.CSSProperties = {
    ...contentStyle,
    textAlign: isMobile ? 'center' : 'left',
    maxWidth: shouldStackCta ? '100%' : contentStyle.maxWidth,
    margin: isMobile ? '0 auto' : undefined
  };
  const headerRowResponsiveStyle: React.CSSProperties = {
    ...headerRowStyle,
    flexWrap: isMobile ? 'wrap' : 'nowrap',
    justifyContent: isMobile ? 'center' : 'flex-start'
  };
  const headlineResponsiveStyle: React.CSSProperties = {
    ...headlineStyle,
    fontSize: isMobile ? '1.2rem' : '1.18rem',
    margin: isMobile ? '0 auto' : '0 0 0.25rem 0'
  };
  const supportingLineResponsiveStyle: React.CSSProperties = {
    ...supportingLineStyle,
    fontSize: isMobile ? '0.94rem' : '0.92rem',
    margin: isMobile ? '0 auto' : undefined
  };
  const buttonColumnStyle: React.CSSProperties = {
    flex: isMobile || shouldStackCta ? '0 0 100%' : '0 0 160px',
    width: isMobile || shouldStackCta ? '100%' : 160,
    textAlign: isMobile || shouldStackCta ? 'center' : undefined
  };
  const buttonGroupResponsiveStyle = {
    ...buttonGroupStyle,
    alignItems: isMobile || shouldStackCta ? 'center' : 'stretch',
    transform: isMobile || shouldStackCta ? undefined : 'translateX(-0.35rem)'
  };

  const trackUpsellClick = (e: React.MouseEvent<HTMLElement>, part: TAwcUpsellClickPart) => {
    RIO_TRACK_EVENT('event', 'internal_click', {
      link_url: getTrackedInternalLinkUrl(selectedCup.watchUrl),
      link_text: t(`${NAMESPACE}:headline`),
      link_classes: e.currentTarget.className || '',
      link_id: `${ANALYTICS_PREFIX}:${part}:${selectedCup.analyticsKey}`,
      content_type: 'upsell',
      content_id: `${ANALYTICS_PREFIX}:${selectedCup.analyticsKey}`,
      upsell_id: ANALYTICS_PREFIX,
      upsell_part: part,
      upsell_variant: selectedCup.analyticsKey,
      upsell_visible_statuses: visibleStatusAnalytics
    });
  };

  const handlePanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    trackUpsellClick(e, 'body');

    navigateTo(selectedCup.watchUrl, e);
  };

  const handleTextClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    trackUpsellClick(e, 'text');

    navigateTo(selectedCup.watchUrl, e);
  };

  const syncVariantStatuses = () => {
    setCurrentTime(Date.now());
  };

  return (
    <div className={props.className}>
      <div
        ref={panelImpressionRef}
        className="slds-p-around--small slds-grid slds-grid--vertical-align-center slds-wrap"
        onClick={handlePanelClick}
        style={panelResponsiveStyle}
      >
        <div className="slds-col slds-shrink-none slds-align--absolute-center rio-m-top-mobi--small">
          <span style={logoWrapResponsiveStyle}>
            <img
              src={asset(LOGO_PATH, { width: 232, height: 232 })}
              alt={t(`${NAMESPACE}:logoAlt`)}
              width={124}
              height={124}
              style={logoResponsiveStyle}
            />
          </span>
        </div>
        <div
          className="slds-col rio-m-top-mobi--small slds-p-horizontal--small"
          onClick={handleTextClick}
          style={contentResponsiveStyle}
        >
          {visibleCupStatuses.length > 0 && (
            <div style={headerRowResponsiveStyle}>
              {visibleCupStatuses.map(({ variant, status }, index) => (
                <span key={variant.analyticsKey} style={getBadgeStyle(status, index === 0)}>
                  {status === 'live' ? (
                    t(`${NAMESPACE}:liveBadge`, { label: variant.label })
                  ) : (
                    <>
                      <i className="fa fa-stopwatch" />
                      <span>{t(`${NAMESPACE}:startsInLabel`, { label: variant.label })}</span>
                      <CountdownTimer
                        className="text-white"
                        to={variant.broadcastStartsAt}
                        formatter={formatCountdownMsTime}
                        onComplete={syncVariantStatuses}
                        tag="span"
                      />
                    </>
                  )}
                </span>
              ))}
            </div>
          )}
          <div className="slds-text-heading--medium text-bold" style={headlineResponsiveStyle}>
            {t(`${NAMESPACE}:headline`)}
          </div>
          <div style={supportingLineResponsiveStyle}>
            <Trans
              i18nKey={`${NAMESPACE}:supportingLine`}
              components={[
                <b style={highlightStyle} />,
                <b style={supportHighlightStyle} />,
                <b style={secondaryHighlightStyle} />
              ]}
            />
          </div>
        </div>
        <div
          className="slds-col slds-grow slds-p-vertical--small slds-p-right--small slds-align--absolute-center rio-m-top-mobi--small"
          style={buttonColumnStyle}
        >
          <div style={buttonGroupResponsiveStyle}>
            <Link
              className={ctaButtonClassName}
              to={selectedCup.watchUrl}
              as={`${ANALYTICS_PREFIX}:button:${selectedCup.analyticsKey}`}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
              style={primaryButtonStyle}
            >
              {t(`${NAMESPACE}:button`)}
            </Link>
            <Link
              to={selectedCup.bracketUrl}
              as={`${ANALYTICS_PREFIX}:text:bracket:${selectedCup.analyticsKey}`}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
              style={supportLinkStyle}
            >
              {t(`${NAMESPACE}:bracketLink`)}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
